import { DOCUMENT } from '@angular/common';
import { provideAppInitializer, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { defaultseoData } from '../data/default-seo-data';

export function provideSeoMetaTags() {
  return provideAppInitializer(() => {
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);
    const titleService = inject(Title);
    const metaService = inject(Meta);
    const document = inject(DOCUMENT);

    router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => activatedRoute),
        // Traverse the route tree to find the primary activated route.
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        // Merge the route data observable.
        mergeMap((route) => route.data),
      )
      .subscribe((data: Data) => {
        const seoData = { ...defaultseoData, ...data };

        const title = seoData.title;
        const description = seoData.description;
        const image = seoData.image;
        const url = seoData.url || 'https://www.codedimension.com.br/';

        titleService.setTitle(title);

        metaService.updateTag(
          { name: 'title', content: title },
          'name="title"',
        );
        metaService.updateTag(
          { name: 'description', content: description },
          'name="description"',
        );

        // Open Graph
        metaService.updateTag(
          { property: 'og:type', content: 'website' },
          'property="og:type"',
        );
        metaService.updateTag(
          { property: 'og:url', content: url },
          'property="og:url"',
        );
        metaService.updateTag(
          { property: 'og:title', content: title },
          'property="og:title"',
        );
        metaService.updateTag(
          { property: 'og:description', content: description },
          'property="og:description"',
        );
        metaService.updateTag(
          { property: 'og:image', content: image },
          'property="og:image"',
        );

        // Twitter
        metaService.updateTag(
          { property: 'twitter:card', content: 'summary_large_image' },
          'property="twitter:card"',
        );
        metaService.updateTag(
          { property: 'twitter:url', content: url },
          'property="twitter:url"',
        );
        metaService.updateTag(
          { property: 'twitter:title', content: title },
          'property="twitter:title"',
        );
        metaService.updateTag(
          { property: 'twitter:description', content: description },
          'property="twitter:description"',
        );
        metaService.updateTag(
          { property: 'twitter:image', content: image },
          'property="twitter:image"',
        );

        const canonicalLinks = Array.from(
          document.head.querySelectorAll<HTMLLinkElement>(
            'link[rel="canonical"]',
          ),
        );

        const canonicalLink =
          canonicalLinks.shift() ?? document.createElement('link');

        canonicalLink.setAttribute('rel', 'canonical');
        canonicalLink.setAttribute('href', url);
        if (!canonicalLink.parentNode) {
          document.head.appendChild(canonicalLink);
        }
        canonicalLinks.forEach((link) => link.remove());
      });
  });
}
